import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";

import { uploadThemeMedia } from "../../functions/uploadThemeMedia";
import { getDvSourceImageUrl } from "../../../functions/themeAssets";
import { renameFileForUpload } from "./safeUploadFileName";

const MAX_FILE_MB = 5;
const MAX_SOUND_MB = 2;
const MAX_CARS = 5;
const MAX_OBSTACLES = 5;

const ROAD_EXT = ["jpg", "jpeg", "png", "gif"];
const CAR_EXT = ["png", "gif"];
const OBSTACLE_EXT = ["jpg", "jpeg", "png", "gif"];
const RACE_SOUND_EXT = ["mp3", "m4a", "wav", "ogg"];

/** Required pixel sizes for race assets. */
const ROAD_DIMS = { width: 400, height: 1280, label: "400×1280px" };
const CAR_DIMS = { width: 129, height: 261, label: "129×261px" };
const OBSTACLE_DIMS = { width: 129, height: 261, label: "129×261px" };

function parseStringArray(value) {
  if (Array.isArray(value)) return value.map((v) => String(v ?? "").trim()).filter(Boolean);
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed)
        ? parsed.map((v) => String(v ?? "").trim()).filter(Boolean)
        : [];
    } catch {
      return value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
  }
  return [];
}

function isUploadToken(value) {
  return typeof value === "string" && value.startsWith("__UPLOAD__:");
}

function getTokenFromValue(value) {
  return isUploadToken(value) ? value.replace("__UPLOAD__:", "") : null;
}

function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image file."));
    };
    img.src = url;
  });
}

function validateImageFile(file, allowedExt) {
  if (!file) return "Please choose an image file.";
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  if (!allowedExt.includes(ext)) {
    return `Unsupported format for ${file.name}. Allowed: ${allowedExt.join(", ").toUpperCase()}.`;
  }
  if (file.size > MAX_FILE_MB * 1024 * 1024) {
    return `${file.name} exceeds ${MAX_FILE_MB}MB.`;
  }
  return null;
}

async function validateImageDimensions(file, dims) {
  try {
    const { width, height } = await getImageDimensions(file);
    if (width !== dims.width || height !== dims.height) {
      return `The dimension should be ${dims.label}.`;
    }
    return null;
  } catch {
    return "Failed to validate image dimensions. Please choose a valid image file.";
  }
}

export default function GameImageTab({
  themeData,
  adminToken,
  currentTheme,
  isSaving,
  setIsSaving,
  onSaved,
}) {
  const [roadImage, setRoadImage] = useState("");
  const [carImages, setCarImages] = useState([]);
  const [obstacleImages, setObstacleImages] = useState([]);
  const [raceSound, setRaceSound] = useState("");
  const [uploadFilesByToken, setUploadFilesByToken] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    if (!themeData) return;
    setRoadImage(String(themeData.road_image ?? "").trim());
    setCarImages(parseStringArray(themeData.car_images));
    setObstacleImages(parseStringArray(themeData.obstacle_images));
    setRaceSound(String(themeData.race_sound ?? "").trim());
    setUploadFilesByToken({});
    setErrorMessage("");
    setSuccessMessage("");
  }, [themeData]);

  const uploadBlobSrcByToken = useMemo(() => {
    const next = {};
    Object.entries(uploadFilesByToken).forEach(([token, file]) => {
      if (file) next[token] = URL.createObjectURL(file);
    });
    return next;
  }, [uploadFilesByToken]);

  useEffect(
    () => () => {
      Object.values(uploadBlobSrcByToken).forEach((url) => URL.revokeObjectURL(url));
    },
    [uploadBlobSrcByToken]
  );

  const getImageSrcFromValue = (value) => {
    const token = getTokenFromValue(value);
    if (token) return uploadBlobSrcByToken[token] || "";
    return value ? getDvSourceImageUrl(value) : "";
  };

  const getAudioSrcFromValue = (value) => {
    const token = getTokenFromValue(value);
    if (token) return uploadBlobSrcByToken[token] || "";
    return value ? getDvSourceImageUrl(value) : "";
  };

  const pickFile = async (file, allowedExt, dims, onAccepted) => {
    if (!file) return;
    const formatError = validateImageFile(file, allowedExt);
    if (formatError) {
      Swal.fire("Invalid file", formatError, "error");
      return;
    }
    const dimError = await validateImageDimensions(file, dims);
    if (dimError) {
      Swal.fire("Invalid dimensions", dimError, "error");
      return;
    }
    const renamed = renameFileForUpload(file, "image");
    const token = `gameimg_${Date.now()}_${Math.floor(Math.random() * 1_000_000)}`;
    setUploadFilesByToken((prev) => ({ ...prev, [token]: renamed }));
    onAccepted(`__UPLOAD__:${token}`);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const onPickRoad = (file) => {
    pickFile(file, ROAD_EXT, ROAD_DIMS, (tokenValue) => setRoadImage(tokenValue));
  };

  const onPickCar = (index, file) => {
    pickFile(file, CAR_EXT, CAR_DIMS, (tokenValue) => {
      setCarImages((prev) => prev.map((item, i) => (i === index ? tokenValue : item)));
    });
  };

  const onPickObstacle = (index, file) => {
    pickFile(file, OBSTACLE_EXT, OBSTACLE_DIMS, (tokenValue) => {
      setObstacleImages((prev) => prev.map((item, i) => (i === index ? tokenValue : item)));
    });
  };

  const onPickRaceSound = (file) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!RACE_SOUND_EXT.includes(ext)) {
      Swal.fire(
        "Invalid file",
        `Unsupported format for ${file.name}. Allowed: ${RACE_SOUND_EXT.join(", ").toUpperCase()}.`,
        "error"
      );
      return;
    }
    if (file.size > MAX_SOUND_MB * 1024 * 1024) {
      Swal.fire("Invalid file", `${file.name} exceeds ${MAX_SOUND_MB}MB.`, "error");
      return;
    }
    const renamed = renameFileForUpload(file, "audio");
    const token = `racesound_${Date.now()}_${Math.floor(Math.random() * 1_000_000)}`;
    setUploadFilesByToken((prev) => ({ ...prev, [token]: renamed }));
    setRaceSound(`__UPLOAD__:${token}`);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const clearRaceSound = () => {
    setRaceSound("");
    setErrorMessage("");
    setSuccessMessage("");
  };

  const addCarSlot = () => {
    if (carImages.length >= MAX_CARS) {
      Swal.fire("Limit reached", `Maximum ${MAX_CARS} car images allowed.`, "warning");
      return;
    }
    setCarImages((prev) => [...prev, ""]);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const addObstacleSlot = () => {
    if (obstacleImages.length >= MAX_OBSTACLES) {
      Swal.fire("Limit reached", `Maximum ${MAX_OBSTACLES} obstacle images allowed.`, "warning");
      return;
    }
    setObstacleImages((prev) => [...prev, ""]);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const removeCarSlot = (index) => {
    setCarImages((prev) => prev.filter((_, i) => i !== index));
  };

  const removeObstacleSlot = (index) => {
    setObstacleImages((prev) => prev.filter((_, i) => i !== index));
  };

  const collectReferencedTokens = () => {
    const values = [roadImage, raceSound, ...carImages, ...obstacleImages];
    return values.map(getTokenFromValue).filter(Boolean);
  };

  const validateMissing = () => {
    const missing = [];
    if (!String(roadImage ?? "").trim()) {
      missing.push("Road image: upload 1 road image");
    } else {
      const token = getTokenFromValue(roadImage);
      if (token && !uploadFilesByToken[token]) {
        missing.push("Road image: re-upload the file");
      }
    }

    const filledCars = carImages.filter((v) => String(v ?? "").trim());
    if (!filledCars.length) {
      missing.push("Car images: upload at least 1 car image (PNG/GIF)");
    }
    carImages.forEach((val, idx) => {
      const v = String(val ?? "").trim();
      if (!v) {
        missing.push(`Car ${idx + 1}: upload a PNG/GIF image`);
        return;
      }
      const token = getTokenFromValue(v);
      if (token && !uploadFilesByToken[token]) {
        missing.push(`Car ${idx + 1}: re-upload the file`);
      }
    });

    const filledObstacles = obstacleImages.filter((v) => String(v ?? "").trim());
    if (!filledObstacles.length) {
      missing.push("Obstacles: upload at least 1 obstacle image");
    }
    obstacleImages.forEach((val, idx) => {
      const v = String(val ?? "").trim();
      if (!v) {
        missing.push(`Obstacle ${idx + 1}: upload an image`);
        return;
      }
      const token = getTokenFromValue(v);
      if (token && !uploadFilesByToken[token]) {
        missing.push(`Obstacle ${idx + 1}: re-upload the file`);
      }
    });

    if (carImages.length > MAX_CARS) {
      missing.push(`Car images: maximum ${MAX_CARS} allowed`);
    }
    if (obstacleImages.length > MAX_OBSTACLES) {
      missing.push(`Obstacles: maximum ${MAX_OBSTACLES} allowed`);
    }

    const soundToken = getTokenFromValue(raceSound);
    if (soundToken && !uploadFilesByToken[soundToken]) {
      missing.push("Car race sound: re-upload the audio file");
    }

    return missing;
  };

  const handleSave = async () => {
    if (!currentTheme) {
      Swal.fire("Error", "No theme selected", "error");
      return;
    }
    if (!adminToken) {
      Swal.fire("Error", "Admin session missing", "error");
      return;
    }

    const missing = validateMissing();
    if (missing.length) {
      Swal.fire({
        icon: "warning",
        title: "Missing uploads",
        html: `<div style="text-align:left">Please upload the missing images before saving:<ul style="margin:10px 0 0 18px">${missing
          .map((m) => `<li>${String(m).replace(/</g, "&lt;").replace(/>/g, "&gt;")}</li>`)
          .join("")}</ul></div>`,
      });
      return;
    }

    setIsSaving(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const formData = new FormData();
      formData.append("themeName", currentTheme);
      formData.append(
        "extraData",
        JSON.stringify({
          road_image: roadImage,
          car_images: carImages.map((v) => String(v ?? "").trim()).filter(Boolean),
          obstacle_images: obstacleImages.map((v) => String(v ?? "").trim()).filter(Boolean),
          race_sound: String(raceSound ?? "").trim(),
        })
      );

      collectReferencedTokens().forEach((token) => {
        const file = uploadFilesByToken[token];
        if (file) formData.append(token, file);
      });

      await uploadThemeMedia({ formData, token: adminToken });
      await onSaved?.();
      setUploadFilesByToken({});
      setSuccessMessage("Game images saved to theme.");
      Swal.fire("Success", "Game images saved to theme.", "success");
    } catch (err) {
      const message = err?.message || "Failed to save game images.";
      setErrorMessage(message);
      Swal.fire("Error", message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const renderSlot = ({
    label,
    value,
    accept,
    onPick,
    onRemove,
    canRemove,
  }) => {
    const src = getImageSrcFromValue(value);
    return (
      <div className="gi-slot">
        <div className="gi-slot-header">
          <span className="gi-slot-label">{label}</span>
          {canRemove ? (
            <button
              type="button"
              className="gi-remove-btn"
              onClick={onRemove}
              disabled={isSaving}
              title="Remove"
              aria-label={`Remove ${label}`}
            >
              ×
            </button>
          ) : null}
        </div>
        <div className="gi-preview-box">
          {src ? (
            <img src={src} alt={label} />
          ) : (
            <span className="gi-placeholder">No image</span>
          )}
        </div>
        <label className="gi-upload-btn">
          <i className="fa-solid fa-upload" aria-hidden /> Upload
          <input
            type="file"
            accept={accept}
            className="gi-upload-input"
            disabled={isSaving}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              onPick(file);
            }}
          />
        </label>
      </div>
    );
  };

  return (
    <div className="themeupdate-panel-wide gi-panel" data-tour="tour-gc-game-images">
      <div className="gi-page-header">
        <h2 className="gi-page-title">Game Images</h2>
        <button
          type="button"
          className="qa-btn qa-btn-primary"
          data-tour="tour-gc-save-images"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? "Saving…" : "Save Game Images"}
        </button>
      </div>

      {(errorMessage || successMessage) && (
        <div className="qa-alerts gi-alerts">
          {errorMessage ? <p className="cr-alert cr-alert-error">{errorMessage}</p> : null}
          {successMessage ? <p className="cr-alert cr-alert-success">{successMessage}</p> : null}
        </div>
      )}

      <div className="gi-columns">
        <section className="gi-section" data-tour="tour-gc-road">
          <div className="gi-section-header">
            <h3 className="gi-section-title">Road</h3>
            <span className="gi-count">{roadImage ? 1 : 0}/1</span>
          </div>
          <p className="gi-helper">
            JPG/PNG/GIF · required size {ROAD_DIMS.label} · max {MAX_FILE_MB}MB
          </p>
          <div className="gi-grid gi-grid--single">
            {renderSlot({
              label: "Road",
              value: roadImage,
              accept: ROAD_EXT.map((e) => `.${e}`).join(","),
              onPick: onPickRoad,
              canRemove: false,
            })}
          </div>
        </section>

        <section className="gi-section" data-tour="tour-gc-cars">
          <div className="gi-section-header">
            <h3 className="gi-section-title">Cars</h3>
            <div className="gi-section-actions">
              <span className="gi-count">
                {carImages.length}/{MAX_CARS}
              </span>
              <button
                type="button"
                className="gi-add-btn"
                onClick={addCarSlot}
                disabled={isSaving || carImages.length >= MAX_CARS}
              >
                + Add
              </button>
            </div>
          </div>
          <p className="gi-helper">
            PNG/GIF · required size {CAR_DIMS.label} · max {MAX_CARS} · {MAX_FILE_MB}MB each
          </p>
          {carImages.length === 0 ? (
            <p className="gi-empty">Click + Add to upload a car.</p>
          ) : (
            <div className="gi-grid">
              {carImages.map((value, index) =>
                renderSlot({
                  label: `Car ${index + 1}`,
                  value,
                  accept: CAR_EXT.map((e) => `.${e}`).join(","),
                  onPick: (file) => onPickCar(index, file),
                  onRemove: () => removeCarSlot(index),
                  canRemove: true,
                })
              )}
            </div>
          )}
        </section>

        <section className="gi-section">
          <div className="gi-section-header">
            <h3 className="gi-section-title">Obstacles</h3>
            <div className="gi-section-actions">
              <span className="gi-count">
                {obstacleImages.length}/{MAX_OBSTACLES}
              </span>
              <button
                type="button"
                className="gi-add-btn"
                onClick={addObstacleSlot}
                disabled={isSaving || obstacleImages.length >= MAX_OBSTACLES}
              >
                + Add
              </button>
            </div>
          </div>
          <p className="gi-helper">
            JPG/PNG/GIF · required size {OBSTACLE_DIMS.label} · max {MAX_OBSTACLES} ·{" "}
            {MAX_FILE_MB}MB each
          </p>
          {obstacleImages.length === 0 ? (
            <p className="gi-empty">Click + Add to upload an obstacle.</p>
          ) : (
            <div className="gi-grid">
              {obstacleImages.map((value, index) =>
                renderSlot({
                  label: `Obs ${index + 1}`,
                  value,
                  accept: OBSTACLE_EXT.map((e) => `.${e}`).join(","),
                  onPick: (file) => onPickObstacle(index, file),
                  onRemove: () => removeObstacleSlot(index),
                  canRemove: true,
                })
              )}
            </div>
          )}
        </section>

        <section className="gi-section" data-tour="tour-gc-race-sound">
          <div className="gi-section-header">
            <h3 className="gi-section-title">Car race sound</h3>
            <span className="gi-count">{raceSound ? 1 : 0}/1</span>
          </div>
          <p className="gi-helper">
            Optional looping engine/race bed · MP3/M4A/WAV/OGG · max {MAX_SOUND_MB}MB ·
            used in-game while racing (falls back to default if empty)
          </p>
          <div className="gi-grid gi-grid--single">
            <div className="gi-slot">
              <div className="gi-slot-header">
                <span className="gi-slot-label">Race sound</span>
                {raceSound ? (
                  <button
                    type="button"
                    className="gi-remove-btn"
                    onClick={clearRaceSound}
                    disabled={isSaving}
                    title="Remove"
                    aria-label="Remove race sound"
                  >
                    ×
                  </button>
                ) : null}
              </div>
              <div className="gi-preview-box gi-preview-box--audio">
                {getAudioSrcFromValue(raceSound) ? (
                  <audio
                    controls
                    src={getAudioSrcFromValue(raceSound)}
                    preload="metadata"
                    className="gi-audio-player"
                  >
                    Your browser does not support audio.
                  </audio>
                ) : (
                  <span className="gi-placeholder">No sound (default in-game)</span>
                )}
              </div>
              <label className="gi-upload-btn">
                <i className="fa-solid fa-upload" aria-hidden /> Upload
                <input
                  type="file"
                  accept={RACE_SOUND_EXT.map((e) => `.${e}`).join(",")}
                  className="gi-upload-input"
                  disabled={isSaving}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    onPickRaceSound(file);
                  }}
                />
              </label>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
